import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useCustomSendVerificationEmailMutation } from '../../redux/apis/endpoints/authApi'
import Button from '../../components/ui/Button'
import FormInput from '../../components/ui/FormInput'
import { 
  ExclamationCircleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'

const CheckEmail = () => {
  const location = useLocation()
  const [sendVerificationEmail, { isLoading, error: resendError }] = useCustomSendVerificationEmailMutation()
  // Initialize message from location state if available
  const [message, setMessage] = useState(location.state?.message || null)
  const [error, setError] = useState(null)
  const [emailSent, setEmailSent] = useState(false)
  const [debugInfo, setDebugInfo] = useState(null)
  const [showDebug, setShowDebug] = useState(false)
  
  // Rate limiting and attempt tracking
  const [attemptCount, setAttemptCount] = useState(0)
  const [lastAttemptTime, setLastAttemptTime] = useState(0)
  const [remainingCooldown, setRemainingCooldown] = useState(0)
  const cooldownInterval = useRef(null)
  const MAX_ATTEMPTS = 3
  const COOLDOWN_SECONDS = 60

  // Get email from location state (passed during redirect)
  const [inputEmail, setInputEmail] = useState(location.state?.email || '')
  
  // For debugging - log location state when component mounts
  useEffect(() => {
    console.log('CheckEmail - Location State:', location.state)
    console.log('Initial email value:', inputEmail)
  }, [location.state, inputEmail])

  // For debugging - log resendError when it changes
  useEffect(() => {
    if (resendError) {
      console.log('Resend Email Error:', resendError)
    }
  }, [resendError])
  
  // Cooldown timer effect
  useEffect(() => {
    if (remainingCooldown > 0) {
      cooldownInterval.current = setInterval(() => {
        const newRemaining = Math.max(0, Math.floor((lastAttemptTime + (COOLDOWN_SECONDS * 1000) - Date.now()) / 1000))
        setRemainingCooldown(newRemaining)
        
        if (newRemaining === 0) {
          clearInterval(cooldownInterval.current)
        }
      }, 1000)
    }
    
    return () => {
      if (cooldownInterval.current) {
        clearInterval(cooldownInterval.current)
      }
    }
  }, [remainingCooldown, lastAttemptTime])

  const isMaxAttemptsReached = attemptCount >= MAX_ATTEMPTS
  const isCooldownActive = remainingCooldown > 0

  const handleSendVerificationEmail = async () => {
    // Check if on cooldown or max attempts reached
    if (isCooldownActive) {
      setError(`Please wait ${remainingCooldown} seconds before requesting another verification email.`)
      return
    }
    
    if (isMaxAttemptsReached) {
      setError('Maximum number of attempts reached. Please contact support for assistance.')
      return
    }
    
    try {
      setError(null)
      setMessage(null)
      setDebugInfo(null)

      // Trim the email address to remove any whitespace
      const emailToVerify = inputEmail.trim()

      if (!emailToVerify) {
        setError('Please enter your email address to send the verification email.')
        return
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(emailToVerify)) {
        setError('Please enter a valid email address format.')
        return
      }

      // Debug info - log what we're about to send
      console.log('Sending verification email to:', emailToVerify)
      
      // Use our custom email verification endpoint - send email directly as a string
      const result = await sendVerificationEmail(emailToVerify).unwrap()
      
      // Debug info - log successful response
      console.log('Verification email sent successfully:', result)

      // Update attempt count and cooldown timer
      const newAttemptCount = attemptCount + 1
      setAttemptCount(newAttemptCount)
      setLastAttemptTime(Date.now())
      setRemainingCooldown(COOLDOWN_SECONDS)
      
      if (result && result.success) {
        setEmailSent(true)
        setMessage(
          'Verification email has been sent. Please check your inbox and click the link to activate your account. The link will expire in 5 minutes.'
        )
      } else {
        setError('Something went wrong. Please try again or contact support.')
      }
    } catch (err) {
      // Debug info - log detailed error structure
      console.error('Verification email error:', err)
      console.error('Error status:', err.status)
      console.error('Error data:', err.data)
      
      // Set debug info for UI display
      setDebugInfo({
        status: err.status,
        data: err.data,
        message: typeof err.data?.message === 'string' ? err.data.message : JSON.stringify(err.data?.message),
        requestEmail: inputEmail,
        requestEmailTrimmed: inputEmail.trim(),
        requestFormat: 'Direct string (not in object)'
      })
      
      // Handle specific error cases
      if (err.status === 400) {
        if (err.data?.message === 'User not found') {
          setError(`This email address (${inputEmail}) is not registered in our system. Please check the email and try again.`)
        }
        else if (err.data?.message === 'Email already verified') {
          setError(`This email address (${inputEmail}) has already been verified. You can now log in.`)
        }
        else if (typeof err.data?.message === 'string') {
          setError(`Failed to send verification email: ${err.data.message}`)
        } 
        else {
          setError('Failed to send verification email. Please check that you entered the correct email address.')
        }
      } else if (err.status === 429) {
        setError('Too many attempts. Please wait a few minutes before trying again.')
      } else {
        setError('Failed to send verification email. Please try again later or contact support.')
      }
      
      // Only count as an attempt if it's a valid error (not server issues)
      if (err.status !== 500 && err.status !== 503) {
        const newAttemptCount = attemptCount + 1
        setAttemptCount(newAttemptCount)
        setLastAttemptTime(Date.now())
        setRemainingCooldown(COOLDOWN_SECONDS)
      }
    }
  }

  // Handle email input change
  const handleEmailChange = (e) => {
    setInputEmail(e.target.value)
    // Clear error when user starts typing
    if (error) setError(null)
  }

  // Add a warning notice if no email is available
  const showEmailWarning = !location.state?.email && !location.state?.message;
  
  // Check if this is a closet user from URL params
  const urlParams = new URLSearchParams(location.search);
  const isClosetUser = urlParams.get('isClosetUser');

  // Determine background style to match Login component
  const backgroundStyle = isClosetUser
    ? {
        minHeight: '100vh',
        backgroundImage:
          "url('https://res.cloudinary.com/sheyou/image/upload/v1727875894/virtual_closet_5d66ccc54e.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        minHeight: '100vh'
      };

  return (
    <div className="gradient_limeade flex flex-col" style={backgroundStyle}>
      <div className="flex flex-grow items-center justify-center py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div className='sm:mx-auto sm:w-full sm:max-w-md pb-6' >
            <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
              {emailSent ? 'Check your email' : 'Email verification required'}
            </h2>
            {emailSent ? (
              <>
                <p className='mt-2 text-center text-sm text-gray-600'>
                  We've sent a verification email to:
                </p>
                <p className='mt-2 text-center text-lg font-medium text-gray-800'>
                  {inputEmail}
                </p>
              </>
            ) : (
              <>
                <p className='mt-2 text-center text-sm text-gray-600'>
                  Your account requires email verification
                </p>
                <p className='mt-2 text-center text-sm text-gray-600'>
                  Click the button below to send a verification email
                </p>
              </>
            )}
          </div>
          
          {emailSent ? (
            <div className='text-sm text-gray-600'>
              <p className='mb-4'>
                Please check your inbox and click on the verification link to
                activate your account.
              </p>
              <p className='mb-4'>
                <strong>Note:</strong> The verification link will expire in 5 minutes. If you don't see the email, check your spam folder or request a new verification email.
              </p>
            </div>
          ) : (
            <div className='text-sm text-gray-600'>
              <p className='mb-4'>
                Your email address must be verified before you can log in.
              </p>
              <p className='mb-4'>
                We'll send you a link to verify your email address.
              </p>
            </div>
          )}

          {showEmailWarning && (
            <div className='mb-4 rounded-md bg-yellow-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <ExclamationCircleIcon
                    className='h-5 w-5 text-yellow-400'
                    aria-hidden='true'
                  />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-yellow-800'>
                    Enter the email address you used during registration.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email Input Field */}
          <div className="space-y-4">
            <FormInput
              label="Email Address"
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              fluid={true}
              value={inputEmail}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              leftIcon={<EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
              className="text-gray-900 placeholder:text-gray-400"
            />
          </div>

          {message && (
            <div className='mb-4 rounded-md bg-green-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <CheckCircleIcon
                    className='h-5 w-5 text-green-400'
                    aria-hidden='true'
                  />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-green-800'>
                    {message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className='mb-4 rounded-md bg-red-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <ExclamationCircleIcon
                    className='h-5 w-5 text-red-400'
                    aria-hidden='true'
                  />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-red-800'>{error}</p>
                </div>
              </div>
            </div>
          )}

          {isCooldownActive && !error && (
            <div className='mb-4 rounded-md bg-blue-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <InformationCircleIcon
                    className='h-5 w-5 text-blue-400'
                    aria-hidden='true'
                  />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-blue-800'>
                    Please wait {remainingCooldown} seconds before requesting another verification email.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isMaxAttemptsReached && (
            <div className='mb-4 rounded-md bg-red-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <ExclamationCircleIcon
                    className='h-5 w-5 text-red-400'
                    aria-hidden='true'
                  />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-red-800'>
                    Maximum number of attempts reached (3). Please contact support for assistance.
                  </p>
                  <p className='text-sm text-red-700 mt-1'>
                    Email: support@sheyou.com
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className='mt-6'>
            <Button
              type='button'
              onClick={handleSendVerificationEmail}
              isLoading={isLoading}
              className='w-full'
              variant='primary'
              disabled={isLoading || isCooldownActive || isMaxAttemptsReached}
            >
              {emailSent ? 'Resend verification email' : 'Send verification email'}
              {isCooldownActive && ` (${remainingCooldown}s)`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckEmail